import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import './Item_Master_indent.css';
import Header from '../../Header';
import { local_signage, local_url } from '../../Const';

export const ItemMasterIndent = () => {
  // State Management
  const [branchId, setBranchId] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Dropdown data
  const [branches, setBranches] = useState([]);

  // Loading and Error States
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch Branches on component mount
  useEffect(() => {
    fetchBranches();
  }, []);

  // Fetch Item Categories when Branch ID changes
  useEffect(() => {
    if (branchId) {
      fetchCategories();
      // Reset dependent fields
      setSelectedCategory('');
      setItems([]);
    } else {
      setCategories([]);
      setSelectedCategory('');
      setItems([]);
    }
  }, [branchId]);

  // Fetch Items when Category changes
  useEffect(() => {
    if (selectedCategory && branchId) {
      fetchItems();
    } else {
      setItems([]);
    }
  }, [selectedCategory]);

  // API Call: Fetch Branches
  const fetchBranches = async () => {
    setLoadingBranches(true);
    setError('');

    try {
      const token = localStorage.getItem('AuthorizationToken');

      if (!token) {
        setError('Authorization token not found. Please login.');
        setLoadingBranches(false);
        return;
      }

      const response = await axios.get(
        `${local_url}/api/Company/GetBranchList/ALL/SEGMENT/MODERN`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data && Array.isArray(response.data)) {
        setBranches(response.data);
      } else {
        setBranches([]);
      }
    } catch (err) {
      console.error('Error fetching branches:', err);
      setError('Failed to fetch branches. Please try again.');
      setBranches([]);
    } finally {
      setLoadingBranches(false);
    }
  };

  // API Call: Fetch Item Categories (Item Families)
  const fetchCategories = async () => {
    setLoadingCategories(true);
    setError('');

    try {
      const token = localStorage.getItem('AuthorizationToken');

      if (!token) {
        setError('Authorization token not found. Please login.');
        setLoadingCategories(false);
        return;
      }

      const response = await axios.get(
        `${local_signage}/api/v1/fetchitemfamily`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data && Array.isArray(response.data)) {
        setCategories(response.data);
      } else {
        setCategories([]);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to fetch categories. Please try again.');
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  // API Call: Fetch Items by Category
  const fetchItems = async () => {
    setLoadingItems(true);
    setError('');

    try {
      const token = localStorage.getItem('AuthorizationToken');

      if (!token) {
        setError('Authorization token not found. Please login.');
        setLoadingItems(false);
        return;
      }

      // Get ItemFamilyId from selected category
      const categoryObj = categories.find((cat) => cat.ItemFamilyID === selectedCategory);
      const itemFamilyId = categoryObj ? categoryObj.ItemFamilyID : '';

      if (!itemFamilyId) {
        setError('Item Family ID not found for selected category.');
        setLoadingItems(false);
        return;
      }

      const response = await axios.get(
        `${local_signage}/api/v1/fetchbranchitems?BranchId=${branchId}&ItemFamilyId=${itemFamilyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data && Array.isArray(response.data)) {
        // Initialize items with their MakeOrBuy value or empty string
        // Map 'M' to 'Make' and 'B' to 'Buy'
        // Store original value to track if item was already mapped
        const itemsWithSelection = response.data.map((item) => {
          let makeOrBuyValue = '';
          if (item.MakeOrBuy === 'M') {
            makeOrBuyValue = 'Make';
          } else if (item.MakeOrBuy === 'B') {
            makeOrBuyValue = 'Buy';
          }

          return {
            ...item,
            selectedMakeOrBuy: makeOrBuyValue,
            originalMakeOrBuy: item.MakeOrBuy, // Store original value to determine if it's an update
          };
        });
        setItems(itemsWithSelection);
      } else {
        setItems([]);
      }
    } catch (err) {
      console.error('Error fetching items:', err);
      setError('Failed to fetch items. Please try again.');
      setItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  // Handle Make/Buy selection for an item
  const handleMakeOrBuyChange = (itemId, value) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.ItemID === itemId ? { ...item, selectedMakeOrBuy: value } : item
      )
    );
  };

  // Submit data to backend
  const handleSubmit = async () => {
    // Filter items that have Make/Buy selected
    const selectedItems = items.filter((item) => item.selectedMakeOrBuy);

    if (selectedItems.length === 0) {
      setError('Please select Make or Buy for at least one item.');
      return;
    }

    setLoadingSubmit(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('AuthorizationToken');
      const userId = localStorage.getItem('UserID') || 'SYSTEM';

      if (!token) {
        setError('Authorization token not found. Please login.');
        setLoadingSubmit(false);
        return;
      }

      // Prepare payload
      const payload = {
        BranchId: branchId,
        CreatedUserId: userId,
        Items: selectedItems.map((item) => {
          const newMakeOrBuyValue = item.selectedMakeOrBuy === 'Make' ? 'M' : 'B';
          // If originalMakeOrBuy exists and is different from new value, it's an update
          // If originalMakeOrBuy is empty/null, it's an insert
          const isUpdate = item.originalMakeOrBuy && item.originalMakeOrBuy !== '';

          return {
            ItemId: item.ItemID,
            MakeOrBuy: newMakeOrBuyValue,
            DML: isUpdate ? 'U' : 'I', // 'U' for Update if already mapped, 'I' for Insert
          };
        }),
      };

      const response = await axios.post(
        `${local_signage}/api/v1/insertbranchitems`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        setSuccessMessage(
          `Data submitted successfully! ${selectedItems.length} item(s) updated for branch ${branchId}.`
        );

        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      }
    } catch (err) {
      console.error('Error submitting data:', err);
      setError(
        err.response?.data?.message || 'Failed to submit data. Please try again.'
      );
    } finally {
      setLoadingSubmit(false);
    }
  };

  // Clear all selections
  const handleClearAll = () => {
    setItems((prevItems) =>
      prevItems.map((item) => ({ ...item, selectedMakeOrBuy: '' }))
    );
    setSearchQuery('');
  };

  // Filter table data based on search query
  const filteredItems = items.filter((item) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    return (
      item.ItemID?.toLowerCase().includes(query) ||
      item.ItemName?.toLowerCase().includes(query) ||
      item.selectedMakeOrBuy?.toLowerCase().includes(query)
    );
  });

  // Get branch name
  const getBranchName = () => {
    const branch = branches.find((b) => b.branchID === branchId);
    return branch ? branch.branchName : '';
  };

  return (
    <>
  <Header/>
    <div className="item-master-container">
     

      <div className="item-master-content">
        {/* Error Display */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Success Display */}
        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}

        {/* Form Section */}
        <div className="item-master-form">
          <div className="form-row">
            {/* Branch Autocomplete */}
            <div className="form-group">
              <Autocomplete
                options={branches}
                getOptionLabel={(option) => `${option.branchName} (${option.branchID})` || ''}
                value={branches.find((branch) => branch.branchID === branchId) || null}
                onChange={(_, newValue) => {
                  setBranchId(newValue ? newValue.branchID : '');
                }}
                disabled={loadingBranches}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Branch *"
                    placeholder={loadingBranches ? 'Loading...' : 'Select Branch'}
                    variant="outlined"
                    size="small"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.branchID === value.branchID}
              />
            </div>

            {/* Category Autocomplete */}
            <div className="form-group">
              <Autocomplete
                options={categories}
                getOptionLabel={(option) => option.ItemFamilyName || ''}
                value={categories.find((cat) => cat.ItemFamilyID === selectedCategory) || null}
                onChange={(_, newValue) => {
                  setSelectedCategory(newValue ? newValue.ItemFamilyID : '');
                }}
                disabled={!branchId || loadingCategories}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Item Category *"
                    placeholder={loadingCategories ? 'Loading...' : 'Select Category'}
                    variant="outlined"
                    size="small"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.ItemFamilyID === value.ItemFamilyID}
              />
            </div>
          </div>
        </div>

        {/* Item Master Table */}
        <div className="item-master-table-section">
          <div className="table-header">
            <h3>Item Master - Make/Buy Mapping</h3>
            <div className="table-header-actions">
              {items.length > 0 && (
                <>
                  <input
                    type="text"
                    placeholder="Search by item name or ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                  <button className="btn btn-secondary" onClick={handleClearAll}>
                    Clear All
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={handleSubmit}
                    disabled={loadingSubmit}
                  >
                    {loadingSubmit ? 'Submitting...' : 'Submit'}
                  </button>
                </>
              )}
            </div>
          </div>

          {loadingItems ? (
            <div className="loading-message">Loading items...</div>
          ) : items.length > 0 ? (
            <>
              {/* Display selected branch and category */}
              <div className="selection-info">
                <span><strong>Branch:</strong> {getBranchName()}</span>
                <span><strong>Category:</strong> {categories.find(c => c.ItemFamilyID === selectedCategory)?.ItemFamilyName}</span>
              </div>

              {filteredItems.length > 0 ? (
                <div className="table-wrapper">
                  <table className="item-master-table">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Item ID</th>
                        <th>Item Name</th>
                        <th>Make/Buy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.map((item, index) => (
                        <tr key={item.ItemID}>
                          <td>{index + 1}</td>
                          <td>{item.ItemID}</td>
                          <td>{item.ItemName}</td>
                          <td>
                            <Autocomplete
                              options={['Make', 'Buy']}
                              value={item.selectedMakeOrBuy || null}
                              onChange={(_, newValue) => {
                                handleMakeOrBuyChange(item.ItemID, newValue || '');
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Select"
                                  variant="outlined"
                                  size="small"
                                  className={!item.selectedMakeOrBuy ? 'empty-autocomplete' : ''}
                                />
                              )}
                              disableClearable={false}
                              className="make-buy-autocomplete"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-data-message">
                  No results found for "{searchQuery}". Try a different search term.
                </div>
              )}
            </>
          ) : selectedCategory ? (
            <div className="no-data-message">
              No items found for this category
            </div>
          ) : (
            <div className="no-data-message">
              Please select Branch and Category to view items
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default ItemMasterIndent;
